import React, { createContext, useContext, useRef, useCallback } from 'react';

// ⭐ 경로 + state를 함께 저장하는 스택 아이템
interface StackItem {
    path: string;
    state?: Record<string, any>;
}

interface NavigationContextType {
    push: (path: string, state?: Record<string, any>) => void;
    pop: () => StackItem | undefined;
    peek: () => StackItem | undefined;
    canGoBack: () => boolean;
    reset: (initialPath?: string) => void;
    getStack: () => StackItem[];
    replaceTop: (path: string, state?: Record<string, any>) => void;
}

const NavigationContext = createContext<NavigationContextType | null>(null);

const ROOT_PATHS = ['/', '/booking', '/notifications', '/profile'];

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const stackRef = useRef<StackItem[]>([{ path: '/' }]);

    const push = useCallback((path: string, state?: Record<string, any>) => {
        const stack = stackRef.current;

        // ⭐ 루트 경로로 이동하면 스택 리셋
        if (ROOT_PATHS.includes(path)) {
            stackRef.current = [{ path, state }];
            console.log('[NavStack] 루트 경로 → 스택 리셋:', stackRef.current);
            return;
        }

        // ⭐ 같은 경로 중복 push 방지 (state가 다르면 업데이트)
        if (stack.length > 0 && stack[stack.length - 1].path === path) {
            if (state) {
                stack[stack.length - 1].state = state;
                console.log('[NavStack] 동일 경로 state 업데이트:', path, state);
            }
            return;
        }

        // ⭐ 스택에 이미 있는 경로로 이동하면 그 지점까지 되감기
        const existingIndex = stack.findIndex(item => item.path === path);
        if (existingIndex !== -1) {
            stackRef.current = stack.slice(0, existingIndex + 1);
            // ⭐ state가 새로 전달된 경우 업데이트
            if (state) {
                stackRef.current[existingIndex].state = state;
            }
            console.log('[NavStack] 기존 경로 발견 → 되감기:', stackRef.current);
            return;
        }

        stack.push({ path, state });
        console.log('[NavStack] push:', path, state, '| 스택:', [...stack]);
    }, []);

    const pop = useCallback((): StackItem | undefined => {
        const stack = stackRef.current;

        if (stack.length <= 1) {
            console.log('[NavStack] pop 불가 - 스택 바닥');
            return undefined;
        }

        stack.pop(); // 현재 페이지 제거
        const prevItem = stack[stack.length - 1]; // 이전 페이지
        console.log('[NavStack] pop → 이동:', prevItem, '| 스택:', [...stack]);
        return prevItem;
    }, []);

    const peek = useCallback((): StackItem | undefined => {
        const stack = stackRef.current;
        return stack.length > 1 ? stack[stack.length - 2] : undefined;
    }, []);

    const canGoBack = useCallback((): boolean => {
        return stackRef.current.length > 1;
    }, []);

    const reset = useCallback((initialPath?: string) => {
        stackRef.current = [{ path: initialPath || '/' }];
        console.log('[NavStack] 스택 리셋:', stackRef.current);
    }, []);

    const getStack = useCallback((): StackItem[] => {
        return [...stackRef.current];
    }, []);

    const replaceTop = useCallback((path: string, state?: Record<string, any>) => {
        const stack = stackRef.current;
        if (stack.length > 0) {
            stack[stack.length - 1] = { path, state };
        } else {
            stack.push({ path, state });
        }
        console.log('[NavStack] replaceTop:', path, state, '| 스택:', [...stack]);
    }, []);

    return (
        <NavigationContext.Provider value={{
            push, pop, peek, canGoBack, reset, getStack, replaceTop
        }}>
            {children}
        </NavigationContext.Provider>
    );
};

export const useNavigationStack = () => {
    const context = useContext(NavigationContext);
    if (!context) {
        throw new Error('useNavigationStack은 NavigationProvider 안에서 사용해야 합니다.');
    }
    return context;
};